import { useState } from 'react'
import type { Item, ItemPriority, CreateItemInput } from '../types'
import SearchableDropdown from './SearchableDropdown'

interface ItemFormProps {
  tripTypeId: string
  existingItems: Item[]
  onSubmit: (data: { item: CreateItemInput | Item, priority: ItemPriority }) => void
  onCancel: () => void
}

const priorityOptions: { value: ItemPriority; label: string; color: string }[] = [
  { value: 'necessary', label: 'Necessary', color: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'good-to-have', label: 'Good to Have', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  { value: 'comfort', label: 'Comfort', color: 'bg-red-100 text-red-800 border-red-200' },
]

export default function ItemForm({ existingItems, onSubmit, onCancel }: ItemFormProps) {
  const [mode, setMode] = useState<'existing' | 'new'>('existing')
  const [selectedItemId, setSelectedItemId] = useState('')
  const [newItemName, setNewItemName] = useState('')
  const [newItemDescription, setNewItemDescription] = useState('')
  const [priority, setPriority] = useState<ItemPriority>('necessary')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (mode === 'existing') {
      const selectedItem = existingItems.find(item => item.id === selectedItemId)
      if (!selectedItem) return
      onSubmit({ item: selectedItem, priority })
    } else {
      if (!newItemName.trim()) return
      const newItem: CreateItemInput = {
        name: newItemName.trim(),
        description: newItemDescription.trim() || undefined,
      }
      onSubmit({ item: newItem, priority })
    }
  }

  const availableItems = existingItems

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Add Item to Trip Type</h3>
      
      {/* Mode Selection */}
      <div className="mb-6">
        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
          <button
            type="button"
            onClick={() => setMode('existing')}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
              mode === 'existing'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Add Existing Item
          </button>
          <button
            type="button"
            onClick={() => setMode('new')}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
              mode === 'new'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Create New Item
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Item Selection/Creation */}
        {mode === 'existing' ? (
          <div>
            <label htmlFor="item" className="block text-sm font-medium text-gray-700 mb-1">
              Select Item *
            </label>
            <SearchableDropdown
              items={availableItems}
              selectedItemId={selectedItemId}
              onSelect={setSelectedItemId}
              placeholder="Search and select an item..."
            />
          </div>
        ) : (
          <>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Item Name *
              </label>
              <input
                type="text"
                id="name"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="e.g., Sunglasses, First Aid Kit"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                value={newItemDescription}
                onChange={(e) => setNewItemDescription(e.target.value)}
                placeholder="Optional description..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>
          </>
        )}

        {/* Priority Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Priority *
          </label>
          <div className="grid grid-cols-3 gap-2">
            {priorityOptions.map((option) => (
              <label
                key={option.value}
                className={`flex items-center justify-center p-3 border rounded-lg cursor-pointer transition-colors ${
                  priority === option.value
                    ? `${option.color} border-current`
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="priority"
                  value={option.value}
                  checked={priority === option.value}
                  onChange={(e) => setPriority(e.target.value as ItemPriority)}
                  className="sr-only"
                />
                <span className="text-sm font-medium">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={mode === 'existing' ? !selectedItemId : !newItemName.trim()}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {mode === 'existing' 
              ? (selectedItemId ? 'Add Selected Item' : 'Select an Item First') 
              : (newItemName.trim() ? 'Create & Add Item' : 'Enter Item Name')
            }
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}